import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './StyledInput.css';
import CancelButton from './CancelButton';

// Organisation classifications that hold several values with one primary:
// Markets (each optionally narrowed to one of its Segments, shown
// "Market › Segment"), Channels (where it sells, e.g. Physical Store, Web
// Store) and Operators (what it does with the products it buys, e.g.
// Retail, Integration, Service). Shared by the Add form
// (CommercialEntityForm.js), the detail page (CommercialEntityDetailPage.js),
// and the Prospects & Customers / All Entities lists.

const SEPARATOR = ' › ';
export const segmentLabel = (segment) => (segment ? `${segment.market?.name || '—'}${SEPARATOR}${segment.name}` : '');
// An Organisation's Market row: "Market", or "Market › Segment".
const marketRowLabel = (r) => (r.market_segment ? `${r.market?.name || '—'}${SEPARATOR}${r.market_segment.name}` : r.market?.name || '');

// Every Market, Segment, Channel and Operator, active and deactivated — the
// filter selects label deactivated ones, the pickers offer only active ones.
export function useClassificationOptions() {
  const [markets, setMarkets] = useState([]);
  const [segments, setSegments] = useState([]);
  const [channels, setChannels] = useState([]);
  const [operators, setOperators] = useState([]);
  useEffect(() => {
    axios.get('/api/markets', { params: { status: 'all' } })
      .then((res) => setMarkets(res.data.data || []))
      .catch(() => setMarkets([]));
    axios.get('/api/market-segments', { params: { status: 'all' } })
      .then((res) => setSegments(res.data.data || []))
      .catch(() => setSegments([]));
    axios.get('/api/channels', { params: { status: 'all' } })
      .then((res) => setChannels(res.data.data || []))
      .catch(() => setChannels([]));
    axios.get('/api/operators', { params: { status: 'all' } })
      .then((res) => setOperators(res.data.data || []))
      .catch(() => setOperators([]));
  }, []);
  return { markets, segments, channels, operators };
}

const primaryOf = (rows) => (rows || []).find((r) => r.is_primary) || (rows || [])[0] || null;

const moreBadgeStyle = { marginLeft: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 16, height: 16, padding: '0 4px', borderRadius: 999, background: '#eceef1', color: '#5a6b85', fontSize: 10, fontWeight: 700, verticalAlign: 'middle' };

// What each list-cell level reads off an entity: its rows, the primary's
// text, and every other row's text (Market de-duplicates, since two rows
// can share one; Segment counts only rows that have a Segment).
const CELL_LEVELS = {
  market: {
    rows: (e) => e.markets,
    text: (r) => r.market?.name,
    dedupe: true,
  },
  segment: {
    rows: (e) => e.markets,
    text: (r) => r.market_segment?.name,
    otherText: (r) => marketRowLabel(r),
    otherRows: (rows) => rows.filter((r) => r.market_segment),
    title: (r) => (r.market_segment ? marketRowLabel(r) : undefined),
  },
  channel: {
    rows: (e) => e.channels,
    text: (r) => r.channel?.name,
  },
  operator: {
    rows: (e) => e.operators,
    text: (r) => r.operator?.name,
  },
};

// List cell: the primary value at this level, with a "+N" badge counting the
// others and naming them on hover.
export function PrimaryClassificationCell({ entity, level }) {
  const cfg = CELL_LEVELS[level];
  const rows = cfg.rows(entity) || [];
  const primary = primaryOf(rows);
  if (!primary) return '—';
  const text = cfg.text(primary);
  const otherText = cfg.otherText || cfg.text;
  const others = cfg.dedupe
    ? [...new Set(rows.map(cfg.text).filter((n) => n && n !== text))]
    : (cfg.otherRows ? cfg.otherRows(rows) : rows).filter((r) => r !== primary).map(otherText);
  if (!text && !others.length) return '—';
  return (
    <span title={cfg.title ? cfg.title(primary) : undefined}>
      {text || '—'}
      {others.length > 0 && (
        <span title={`Also: ${others.join(', ')}`} style={moreBadgeStyle}>+{others.length}</span>
      )}
    </span>
  );
}

// The Market, Market Segment, Channel and Operator filter selects. 'none'
// finds Organisations with nothing at that level — for Segment, that includes
// Market-only ones, narrowed to the chosen Market when there is one.
export function ClassificationFilterFields({ draft, setDraftField, markets, segments, channels, operators, labelStyle, inputStyle }) {
  const shownSegments = draft.market_id && draft.market_id !== 'none'
    ? segments.filter((s) => String(s.market_id) === String(draft.market_id))
    : segments;
  return (
    <>
      <div>
        <label style={labelStyle}>Market</label><br />
        <select
          className="styled-input"
          value={draft.market_id}
          onChange={(e) => {
            setDraftField('market_id', e.target.value);
            setDraftField('market_segment_id', '');
          }}
          style={inputStyle}
        >
          <option value="">All</option>
          <option value="none">(none)</option>
          {markets.map((m) => <option key={m.id} value={m.id}>{m.name}{m.is_active ? '' : ' (deactivated)'}</option>)}
        </select>
      </div>
      {draft.market_id !== 'none' && (
        <div>
          <label style={labelStyle}>Market Segment</label><br />
          <select className="styled-input" value={draft.market_segment_id} onChange={(e) => setDraftField('market_segment_id', e.target.value)} style={inputStyle}>
            <option value="">All</option>
            <option value="none">(none)</option>
            {shownSegments.map((s) => (
              <option key={s.id} value={s.id}>
                {draft.market_id ? s.name : segmentLabel(s)}{s.is_active ? '' : ' (deactivated)'}
              </option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label style={labelStyle}>Operator</label><br />
        <select className="styled-input" value={draft.operator_id} onChange={(e) => setDraftField('operator_id', e.target.value)} style={inputStyle}>
          <option value="">All</option>
          <option value="none">(none)</option>
          {operators.map((o) => <option key={o.id} value={o.id}>{o.name}{o.is_active ? '' : ' (deactivated)'}</option>)}
        </select>
      </div>
      <div>
        <label style={labelStyle}>Outlet</label><br />
        <select className="styled-input" value={draft.channel_id} onChange={(e) => setDraftField('channel_id', e.target.value)} style={inputStyle}>
          <option value="">All</option>
          <option value="none">(none)</option>
          {channels.map((c) => <option key={c.id} value={c.id}>{c.name}{c.is_active ? '' : ' (deactivated)'}</option>)}
        </select>
      </div>
    </>
  );
}

const removeBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#b91c1c', fontSize: 15, lineHeight: 1, padding: 0 };

// An Organisation's held values for one classification (Markets, Operators,
// Channels), one per line ("★" after the Primary once there are two or
// more). Detail page, read-first, with editing driven from the card's kebab:
// `editing` shows ☆ Set as Primary and × per value; `adding` shows the add
// select until a value is picked or Cancel is clicked. `inForm` (the Add
// form) keeps both on, with no Cancel. rows are the entity's join rows;
// onChange gets the next list in the same shape. addOptions:
// [{ value, label, group? }] — options sharing a group render under one
// <optgroup>; onAdd(value) returns the row to append.
export function ClassificationField({ label, rows, labelOf, editing: editingProp, adding: addingProp, inForm, addOptions, onAdd, onCancelAdd, onChange, disabled, labelStyle }) {
  const editing = editingProp || inForm;
  const adding = addingProp || inForm;
  const list = rows || [];
  const showStars = list.length > 1;
  const withPrimary = (next, primaryIndex) => next.map((r, i) => ({ ...r, is_primary: i === primaryIndex }));
  const remove = (index) => {
    const next = list.filter((_, i) => i !== index);
    onChange(list[index]?.is_primary && next.length ? withPrimary(next, 0) : next);
  };
  const add = (value) => {
    if (!value) return;
    onChange([...list, { ...onAdd(value), is_primary: list.length === 0 }]);
  };
  const groups = [];
  for (const o of addOptions || []) {
    const g = groups.find((x) => x.name === (o.group || ''));
    if (g) g.options.push(o); else groups.push({ name: o.group || '', options: [o] });
  }
  const renderOption = (o) => <option key={o.value} value={o.value}>{o.label}</option>;

  return (
    <div style={{ marginBottom: inForm ? 0 : 14 }}>
      {inForm ? <><label>{label}</label><br /></> : <div style={labelStyle}>{label}</div>}
      {list.length === 0 && !adding && <div style={{ fontSize: valueFontSize }}>—</div>}
      {list.length > 0 && !editing && list.map((r, i) => (
        <div key={i} style={{ fontSize: valueFontSize, padding: '1px 0' }}>
          {labelOf(r)}
          {showStars && r.is_primary && <span title="Primary" style={starStyle}> ★</span>}
        </div>
      ))}
      {list.length > 0 && editing && list.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: valueFontSize, padding: '2px 0' }}>
          {showStars && (r.is_primary ? (
            <span title="Primary" style={starStyle}>★</span>
          ) : (
            <button type="button" disabled={disabled} onClick={() => onChange(withPrimary(list, i))} title="Set as Primary" style={{ ...starButtonStyle, color: '#9aa5b5' }}>☆</button>
          ))}
          <span style={{ flex: 1, minWidth: 0 }}>{labelOf(r)}</span>
          <button type="button" disabled={disabled} onClick={() => remove(i)} title={`Remove ${labelOf(r)}`} style={{ ...removeBtnStyle, fontSize: valueFontSize }}>×</button>
        </div>
      ))}
      {adding && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: list.length ? 6 : 0 }}>
          <select
            className="styled-input"
            value=""
            autoFocus={!inForm}
            disabled={disabled || !(addOptions || []).length}
            onChange={(e) => add(e.target.value)}
            style={inForm ? { minWidth: 220, maxWidth: 320, fontSize: valueFontSize } : { flex: 1, minWidth: 0, fontSize: valueFontSize }}
          >
            <option value="">{(addOptions || []).length ? `${inForm ? 'Add' : 'Choose'} ${label.replace(/s$/, '')}…` : 'Nothing to add'}</option>
            {groups.map((g) => (g.name
              ? <optgroup key={g.name} label={g.name}>{g.options.map(renderOption)}</optgroup>
              : g.options.map(renderOption)))}
          </select>
          {!inForm && <CancelButton onClick={onCancelAdd} style={{ padding: '2px 10px', fontSize: 12, marginLeft: 6 }} />}
        </div>
      )}
    </div>
  );
}

const valueFontSize = 14;
const starStyle = { color: '#e0a100' };
const starButtonStyle = { background: 'none', border: 'none', cursor: 'pointer', fontSize: valueFontSize, lineHeight: 1, padding: 0 };

export const marketRowText = marketRowLabel;

// Add options for an Organisation's Markets: each active Market as its own
// group, offering the Market alone and each of its active Segments, less
// whatever the rows already hold. Values are "m:<id>" or "s:<id>".
export function marketAddOptions(rows, markets, segments) {
  const held = rows || [];
  const heldSegmentIds = new Set(held.filter((r) => r.market_segment_id).map((r) => String(r.market_segment_id)));
  const marketOnlyIds = new Set(held.filter((r) => !r.market_segment_id).map((r) => String(r.market_id)));
  const options = [];
  for (const m of markets.filter((x) => x.is_active)) {
    if (!marketOnlyIds.has(String(m.id))) options.push({ value: `m:${m.id}`, label: `${m.name} (no Segment)`, group: m.name });
    for (const s of segments.filter((x) => x.is_active && String(x.market_id) === String(m.id) && !heldSegmentIds.has(String(x.id)))) {
      options.push({ value: `s:${s.id}`, label: s.name, group: m.name });
    }
  }
  return options;
}

// The Market row for an "m:<id>" / "s:<id>" add option.
export function marketRowFor(value, markets, segments) {
  const [kind, id] = value.split(':');
  if (kind === 's') {
    const segment = segments.find((s) => String(s.id) === id);
    const market = markets.find((m) => String(m.id) === String(segment?.market_id));
    return { market_id: Number(segment.market_id), market_segment_id: Number(id), market, market_segment: segment };
  }
  return { market_id: Number(id), market_segment_id: null, market: markets.find((m) => String(m.id) === id), market_segment: null };
}

// Add options for a flat classification (Operators, Channels): active
// values the rows don't already hold.
export function heldAddOptions(options, rows, idKey) {
  const held = new Set((rows || []).map((r) => String(r[idKey])));
  return options.filter((o) => o.is_active && !held.has(String(o.id))).map((o) => ({ value: String(o.id), label: o.name }));
}
